import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
  Card,
  CardContent,
  LinearProgress
} from '@mui/material';
import './EbaySearchCombined.css';
import API_BASE_URL from '../config/apiBaseUrl';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const FULL_SCRAPE_MAX_RESULTS = 5000;

function EbaySearchCombined() {
  const [libelles, setLibelles] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [sellerInput, setSellerInput] = useState('');
  const [saveToDB, setSaveToDB] = useState(true);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');
  const [searchStats, setSearchStats] = useState(null);
  const [totalStats, setTotalStats] = useState(null);
  const [soldItems, setSoldItems] = useState([]);
  const [missingInStock, setMissingInStock] = useState([]);
  const [highRotationMissing, setHighRotationMissing] = useState([]);

  // Charger les libellés et vendeurs au montage
  useEffect(() => {
    fetchLibelles();
    fetchSellers();
  }, []);

  const fetchLibelles = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/libelles?enabled_only=true`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success) {
        setLibelles(data.data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des libellés:', error);
      showMessage('Erreur lors du chargement des libellés', 'error');
    }
  };

  const fetchSellers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ebay/sellers`, { headers: getAuthHeaders() });
      const data = await response.json();
      if (data.success && data.sellers) {
        setSellers(data.sellers);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des vendeurs:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    if (!sellerInput.trim()) {
      showMessage('Veuillez sélectionner un vendeur', 'error');
      return;
    }

    if (libelles.length === 0) {
      showMessage('Aucun libellé disponible en base de données', 'error');
      return;
    }

    setLoading(true);
    setSearchResults([]);
    setSearchStats(null);
    setSoldItems([]);
    setMissingInStock([]);
    setHighRotationMissing([]);
    setTotalStats(null);

    try {
      let allItems = [];
      let totalSaved = 0;
      let totalFound = 0;
      const sellersResults = {};
      const allSoldItems = [];
      const allMissingInStock = [];
      const allHighRotation = [];

      // BOUCLE SÉQUENTIELLE sur chaque libellé (un à la fois)
      for (let i = 0; i < libelles.length; i++) {
        const libelle = libelles[i];
        const progressMsg = `🔍 Scrape ${i + 1}/${libelles.length}: "${libelle.name}" chez ${sellerInput}...`;
        showMessage(progressMsg, 'info');

        try {
          // Appel API avec pagination complète (pas de limit)
          const params = new URLSearchParams({
            libelle: libelle.name,
            seller: sellerInput.trim(),
            maxResults: String(FULL_SCRAPE_MAX_RESULTS),
            saveToDB: saveToDB ? 'true' : 'false'
          });

          const response = await fetch(`${API_BASE_URL}/ebay/search-full?${params.toString()}`, { headers: getAuthHeaders() });
          const data = await response.json();

          if (!response.ok) {
            throw new Error(data.message || `HTTP ${response.status}`);
          }

          if (data.items && data.items.length > 0) {
            allItems = [...allItems, ...data.items];
            totalFound += data.itemsCount || 0;
            totalSaved += data.savedCount || 0;

            // Analyser les pièces vendues (non présentes dans ce scrape vs précédent)
            if (data.soldItems) {
              allSoldItems.push(...data.soldItems);
            }

            // Analyser les pièces manquantes en stock
            if (data.missingInStock) {
              allMissingInStock.push(...data.missingInStock);
            }

            // Pièces à forte rotation manquantes en stock (priorité haute)
            if (data.highRotationMissing) {
              allHighRotation.push(...data.highRotationMissing);
            }

            sellersResults[libelle.name] = {
              count: data.itemsCount,
              saved: data.savedCount,
              message: data.message
            };

            console.log(`✅ ${libelle.name}: ${data.itemsCount} articles trouvés`);
          } else {
            sellersResults[libelle.name] = {
              count: 0,
              saved: 0,
              message: 'Aucun article trouvé'
            };
            console.log(`⚠️ ${libelle.name}: Aucun article`);
          }

          // Attendre un peu entre chaque requête pour ne pas surcharger l'API
          if (i < libelles.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`❌ Erreur pour libellé ${libelle.name}:`, error);
          showMessage(`❌ Erreur pour ${libelle.name}: ${error.message}`, 'error');
        }
      }

      // Résultats finaux
      setSearchResults(allItems);
      setSoldItems(allSoldItems);
      setMissingInStock(allMissingInStock);
      setHighRotationMissing(allHighRotation);

      setTotalStats({
        seller: sellerInput,
        totalFound,
        totalSaved,
        libellésProcessed: libelles.length,
        sellersResults,
        soldItemsCount: allSoldItems.length,
        missingCount: allMissingInStock.length,
        highRotationMissingCount: allHighRotation.length
      });

      showMessage(
        `✅ Scrape terminé: ${totalFound} article(s) trouvé(s) chez ${libelles.length} libellé(s), ${totalSaved} sauvegardé(s)`,
        'success'
      );
    } catch (error) {
      showMessage(`❌ Erreur: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  const formatPrice = (price) => {
    return parseFloat(price).toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getSellerValue = (seller) => {
    if (typeof seller === 'string') {
      return seller;
    }

    return seller?.name || seller?.label || '';
  };

  const getSellerLabel = (seller) => {
    if (typeof seller === 'string') {
      return seller;
    }

    return seller?.label || seller?.name || '';
  };

  return (
    <Container maxWidth="lg" className="ebay-search-combined">
      <Paper className="search-paper" elevation={3}>
        <h2>🔍 Recherche Combinée: Libellé + Vendeur</h2>
        <p className="subtitle">Recherchez un libellé chez un vendeur spécifique sur eBay</p>

        {message && (
          <Alert severity={messageType} style={{ marginBottom: '20px' }}>
            {message}
          </Alert>
        )}

        {/* FORMULAIRE DE RECHERCHE */}
        <Paper className="search-form-paper" elevation={1}>
          <form onSubmit={handleSearch}>
            <Grid container spacing={3}>
              {/* Info: sera scrapé pour TOUS les libellés */}
              <Grid item xs={12}>
                <Alert severity="info">
                  🔄 Scrape en cours: {libelles.length} libellé(s) disponible(s) seront interrogé(s) un à un pour scraper toute la boutique du vendeur
                </Alert>
              </Grid>

              {/* Sélection vendeur */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Vendeur eBay</InputLabel>
                  <Select
                    value={sellerInput}
                    label="Vendeur eBay"
                    onChange={(e) => setSellerInput(e.target.value)}
                    disabled={loading || sellers.length === 0}
                  >
                    <MenuItem value="">-- Sélectionner un vendeur --</MenuItem>
                    {sellers.map((seller, index) => (
                      <MenuItem key={getSellerValue(seller) || index} value={getSellerValue(seller)}>
                        {getSellerLabel(seller)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Sauvegarder en BDD</InputLabel>
                  <Select
                    value={saveToDB}
                    label="Sauvegarder en BDD"
                    onChange={(e) => setSaveToDB(e.target.value)}
                    disabled={loading}
                  >
                    <MenuItem value={true}>✅ Oui, sauvegarder</MenuItem>
                    <MenuItem value={false}>❌ Non, seulement voir</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Bouton recherche */}
              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  size="large"
                  type="submit"
                  disabled={loading || !sellerInput.trim()}
                  className="search-button"
                >
                  {loading ? (
                    <>
                      <CircularProgress size={24} style={{ marginRight: '10px' }} />
                      Scrape en cours...
                    </>
                  ) : (
                    <>
                      🔎 Scraper toute la boutique
                    </>
                  )}
                </Button>
              </Grid>
            </Grid>

            {loading && (
              <Box style={{ marginTop: '20px' }}>
                <LinearProgress />
                <p style={{ textAlign: 'center', marginTop: '10px', color: '#666' }}>
                  Recherche en cours sur eBay France...
                </p>
              </Box>
            )}
          </form>
        </Paper>

        {/* STATS DE RECHERCHE */}
        {searchStats && (
          <Card className="stats-card" style={{ marginTop: '30px', backgroundColor: '#f0f7ff', borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <h3>📊 Résultats de recherche</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <div className="stat-item">
                    <strong>Libellé:</strong>
                    <Chip label={searchStats.libelle} color="primary" />
                  </div>
                </Grid>
                <Grid item xs={12} md={3}>
                  <div className="stat-item">
                    <strong>Vendeur:</strong>
                    <Chip label={searchStats.seller} color="primary" />
                  </div>
                </Grid>
                <Grid item xs={12} md={3}>
                  <div className="stat-item">
                    <strong>Articles trouvés:</strong>
                    <span style={{ fontSize: '18px', color: '#1976d2', fontWeight: 'bold' }}>
                      {searchStats.itemsCount}
                    </span>
                  </div>
                </Grid>
                <Grid item xs={12} md={3}>
                  <div className="stat-item">
                    <strong>Sauvegardés:</strong>
                    <span style={{ fontSize: '18px', color: '#4caf50', fontWeight: 'bold' }}>
                      {searchStats.savedCount}
                    </span>
                  </div>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* TABLEAU RÉSULTATS */}
        {searchResults.length > 0 && (
          <TableContainer component={Paper} style={{ marginTop: '30px' }}>
            <h3 style={{ padding: '20px', paddingBottom: '0' }}>
              📋 Détail des {searchResults.length} articles
            </h3>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Titre</strong></TableCell>
                  <TableCell align="right"><strong>Prix</strong></TableCell>
                  <TableCell align="center"><strong>Condition</strong></TableCell>
                  <TableCell align="center"><strong>Vendeur</strong></TableCell>
                  <TableCell align="center"><strong>Lien</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchResults.map((item, index) => (
                  <TableRow key={item.itemId || index} hover>
                    <TableCell style={{ maxWidth: '300px' }}>
                      <strong>{item.title?.substring(0, 60)}...</strong>
                      {item.image && (
                        <img
                          src={item.image}
                          alt="preview"
                          style={{
                            maxWidth: '50px',
                            maxHeight: '50px',
                            marginTop: '5px',
                            borderRadius: '4px'
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <strong style={{ color: '#d32f2f', fontSize: '16px' }}>
                        {formatPrice(item.price)} €
                      </strong>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.condition}
                        size="small"
                        color={item.condition === 'New' ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">{item.sellerName}</TableCell>
                    <TableCell align="center">
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ebay-link"
                        >
                          🔗 Voir
                        </a>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && message && messageType === 'info' && (
          <Alert severity="info" style={{ marginTop: '20px' }}>
            {message}
          </Alert>
        )}

        {/* STATS TOTALES */}
        {totalStats && (
          <Card style={{ marginTop: '30px', backgroundColor: '#f5f5f5', borderLeft: '4px solid #2196f3' }}>
            <CardContent>
              <h3>📊 Résumé du Scrape Complet</h3>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Box style={{ backgroundColor: '#e3f2fd', padding: '15px', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>VENDEUR</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#1976d2' }}>
                      {totalStats.seller}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box style={{ backgroundColor: '#f3e5f5', padding: '15px', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>LIBELLÉS TRAITÉS</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#7b1fa2' }}>
                      {totalStats.libellésProcessed}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box style={{ backgroundColor: '#e8f5e9', padding: '15px', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>ARTICLES TOTAUX</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#388e3c' }}>
                      {totalStats.totalFound}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box style={{ backgroundColor: '#fff3e0', padding: '15px', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>NOUVEAUX ARTICLES</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f57c00' }}>
                      {totalStats.totalSaved}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box style={{ backgroundColor: '#ffebee', padding: '15px', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>PIÈCES VENDUES</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#c62828' }}>
                      {totalStats.soldItemsCount}
                    </div>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box style={{ backgroundColor: '#fcf3cf', padding: '15px', borderRadius: '4px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>MANQUANTES EN STOCK</div>
                    <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f39c12' }}>
                      {totalStats.missingCount}
                    </div>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* PIÈCES À FORTE ROTATION MANQUANTES (PRIORITÉ HAUTE) */}
        {highRotationMissing.length > 0 && (
          <Card style={{ marginTop: '30px', borderLeft: '4px solid #d32f2f', backgroundColor: '#ffebee' }}>
            <CardContent>
              <h3 style={{ color: '#d32f2f' }}>🔴 PRIORITÉ HAUTE - Pièces à forte rotation manquantes en stock</h3>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#ffcdd2' }}>
                      <TableCell><strong>OEM</strong></TableCell>
                      <TableCell><strong>Titre</strong></TableCell>
                      <TableCell align="right"><strong>Score Rotation</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {highRotationMissing.map((item, idx) => (
                      <TableRow key={idx} style={{ backgroundColor: '#ffebee' }}>
                        <TableCell style={{ fontWeight: 'bold', color: '#d32f2f' }}>{item.oem}</TableCell>
                        <TableCell>{item.title?.substring(0, 50)}...</TableCell>
                        <TableCell align="right" style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                          {item.rotationScore}/10
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* PIÈCES VENDUES */}
        {soldItems.length > 0 && (
          <Card style={{ marginTop: '30px', borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <h3 style={{ color: '#ff9800' }}>✅ Pièces vendues (rotation de stock détectée)</h3>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#ffe0b2' }}>
                      <TableCell><strong>Item ID</strong></TableCell>
                      <TableCell><strong>OEM</strong></TableCell>
                      <TableCell><strong>Titre</strong></TableCell>
                      <TableCell align="right"><strong>Rotation (jours)</strong></TableCell>
                      <TableCell align="right"><strong>Prix</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {soldItems.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell style={{ fontSize: '12px' }}>{item.itemId}</TableCell>
                        <TableCell style={{ fontWeight: 'bold' }}>{item.oem}</TableCell>
                        <TableCell>{item.title?.substring(0, 40)}...</TableCell>
                        <TableCell align="right" style={{ fontWeight: 'bold', color: '#ff9800' }}>
                          {item.rotationDays} j
                        </TableCell>
                        <TableCell align="right">{formatPrice(item.price)} €</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* PIÈCES MANQUANTES EN STOCK (priorité normale) */}
        {missingInStock.length > 0 && (
          <Card style={{ marginTop: '30px', borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <h3 style={{ color: '#ff9800' }}>⚠️ Pièces manquantes en stock</h3>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow style={{ backgroundColor: '#ffe0b2' }}>
                      <TableCell><strong>OEM</strong></TableCell>
                      <TableCell><strong>Titre</strong></TableCell>
                      <TableCell><strong>Vendeur</strong></TableCell>
                      <TableCell align="right"><strong>Prix</strong></TableCell>
                      <TableCell align="right"><strong>Score Rotation</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {missingInStock.map((item, idx) => (
                      <TableRow
                        key={idx}
                        style={{
                          backgroundColor: item.isHighPriority ? '#ffccbc' : '#fff3e0'
                        }}
                      >
                        <TableCell style={{ fontWeight: 'bold' }}>{item.oem}</TableCell>
                        <TableCell>{item.title?.substring(0, 40)}...</TableCell>
                        <TableCell>{item.seller}</TableCell>
                        <TableCell align="right">{formatPrice(item.price)} €</TableCell>
                        <TableCell
                          align="right"
                          style={{
                            fontWeight: 'bold',
                            color: item.isHighPriority ? '#d32f2f' : '#ff9800'
                          }}
                        >
                          {item.rotationScore.toFixed(1)}/10
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Paper>
    </Container>
  );
}

export default EbaySearchCombined;

